import React from 'react';
import { withStyles, WithStyles, Theme, createStyles } from '@material-ui/core';

import { withIntl, IntlProps } from 'ha/i18n';
import MediaQuery from 'ha/helpers/MediaQuery';
import {
  VirtualizeSliderWithControls,
  SlideRendererParams,
  ControlProps,
} from 'ha/components/next/Slider';
import { Container, Typography, units } from '@ha/dsl';
import CityCard from 'ha/components/next/CityCard';
import SliderControl from './SliderControl';
import { TopCity, CityListingsCountsMap } from '../../types';

export const styles = (t: Theme) =>
  createStyles({
    wrapper: {
      padding: units.yx(3.375, 0),
      backgroundColor: '#f8f9fa',
      [t.breakpoints.up('md')]: {
        padding: units.txb(3, 0, 8),
      },
    },
    title: {
      fontSize: units.rem(1.5),
      marginBottom: units.rem(0.75),
      [t.breakpoints.up('md')]: {
        fontSize: units.rem(1.875),
        marginBottom: units.rem(1.25),
      },
    },
    subtitle: {
      fontWeight: 'normal',
      fontSize: units.rem(1),
      marginBottom: units.rem(1.5),
      [t.breakpoints.up('md')]: {
        marginBottom: units.rem(2.5),
      },
    },
    leftControl: {
      marginRight: units.rem(2),
    },
    rightControl: {
      marginLeft: units.rem(2),
    },
  });

interface Props {
  list: TopCity[];
  listingsCounts?: CityListingsCountsMap;
}

export const TopCities: React.FC<
  Props & IntlProps & WithStyles<typeof styles>
> = ({ list, listingsCounts, intl: { T, lang }, classes }) => (
  <div className={classes.wrapper}>
    <Container maxWidth="lg">
      <Typography.H2
        component="h2"
        align="center"
        className={classes.title}
        data-test-locator="Home TopCities Title"
      >
        {T('home_page_top_cities_title')}
      </Typography.H2>
      <Typography.H4
        color="muted"
        align="center"
        className={classes.subtitle}
        data-test-locator="Home TopCities Subtitle"
        component="h3"
      >
        {T('home_page_top_cities_subtitle')}
      </Typography.H4>
      <MediaQuery>
        {breakpoints => {
          const isMobile = !breakpoints.md;

          const controls = {
            ...(!isMobile && {
              leftControl: ({ previousSlide }: ControlProps) => (
                <div className={classes.leftControl}>
                  <SliderControl
                    data-test-locator="Home TopCities Left Control"
                    direction="left"
                    previousSlide={previousSlide}
                  />
                </div>
              ),
              rightControl: ({ nextSlide }: ControlProps) => (
                <div className={classes.rightControl}>
                  <SliderControl
                    data-test-locator="Home TopCities Right Control"
                    direction="right"
                    nextSlide={nextSlide}
                  />
                </div>
              ),
            }),
          };

          return (
            <VirtualizeSliderWithControls
              data-test-locator="Home TopCities Slider"
              slideCount={list.length}
              overscanSlideAfter={list.length - 1}
              slideRenderer={({ index, key }: SlideRendererParams) => {
                const city = list[index];
                const testLocator = `Home TopCities CityCard ${city.name}`;

                return (
                  <CityCard
                    testLocator={testLocator}
                    key={key}
                    listingsCount={listingsCounts && listingsCounts[city.name]}
                    url={city.urls[lang]}
                    imageUrl={city.imageUrl}
                    name={T(city.name)}
                    description={T(city.description)}
                  />
                );
              }}
              {...controls}
              style={{
                width: units.rem(16.25),
                overflowX: 'visible',
              }}
              slideStyle={{
                padding: units.rem(0.5),
              }}
            />
          );
        }}
      </MediaQuery>
    </Container>
  </div>
);

export default withStyles(styles)(withIntl(TopCities));
